import { useInitiallyHiddenElement } from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { StatusIcon, StatusMessageFieldName } from '../../icons/StatusIcons';
import { TabStyles } from '../../styles/TabStyles';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { AlertBaseColorStyleName, AlertType as ToastType, ColorMap } from '../AlertStyles';
import { CloseButtonWidget } from '../Button/CloseButtonWidget';
import { TextLinkKind, TextLinkWidget } from '../TextLinkWidget';

export { ToastType };

const DefaultActionHref = '#';

export interface ToastBaseProps {
  /**
   * This href will navigate to the url provided in a new tab if no `onAction` callback is provided.
   * If `onAction` is provided, it will display in browser on focus/hover, and will be accessible in the right-click menu, but it will not navigate on click.
   *
   * @deprecated Action links are not accessible.
   * Usually action links are not necessary, but if needed work with UX to find a different pattern (such as `InlineAlertWidget` with a `ButtonWidget`).
   */
  actionHref?: string;
  /**
   * Message of the action link
   *
   * @deprecated Action links are not accessible.
   * Usually action links are not necessary, but if needed work with UX to find a different pattern (such as `InlineAlertWidget` with a `ButtonWidget`).
   */
  actionMessage?: string;
  /**
   * `ToastWidget` expects a `string` value for the `message` prop for proper formatting of its text.
   * More complex content may be passed here in `children`, but caveat emptor: the layout/styling of the Toast may break as a result.
   */
  children?: React.ReactNode;
  /** Main message on the Toast */
  message: string;
  /**
   * Called when action link is clicked
   *
   * @deprecated Action links are not accessible.
   * Usually action links are not necessary, but if needed work with UX to find a different pattern (such as `InlineAlertWidget` with a `ButtonWidget`).
   */
  onAction?: () => void;
  /** Called when Toast is closed */
  onCloseRequest?: () => void;
  /** Test id to be attached to the widget */
  testId: string;
  /** The type of Toast you want to display. This affects style of the Toast. */
  toastType: ToastType;
}

export const ToastDivTestIdSuffix: string = 'toast-widget';
export const ToastContentTestIdSuffix: string = 'toast-content';
export const ToastMessageTestIdSuffix: string = 'toast-message';

export const ToastHeight = 40;
export const ToastSpacing = 8;
const { Default: FontSize } = TabStyles.Typography.Sizing;

const ToastRootStyleName = TypeStyle.style({
  alignItems: 'flex-start',
  border: '1px solid transparent',
  borderLeftWidth: 8,
  borderRadius: 4,
  boxShadow: TabStyles.Shadow.Standard,
  boxSizing: 'border-box',
  color: '#333',
  display: 'inline-flex',
  flexDirection: 'row',
  fontSize: FontSize,
  justifyContent: 'flex-start',
  marginBottom: 3,
  minHeight: ToastHeight,
  padding: `10px ${ToastSpacing * 2}px 10px ${ToastSpacing}px`,
  position: 'relative',
  visibility: 'visible',
});

const StatusIconStyleName = TypeStyle.style({
  marginTop: 2,
});

const ContentDivStyleName = TypeStyle.style({
  display: 'flex',
  lineHeight: 1.5,
});

const MessageContainerBaseStyleName = TypeStyle.style({
  display: 'flex',
  flexWrap: 'wrap',
  marginBottom: -ToastSpacing,
  position: 'relative',
  $nest: {
    '&::after': {
      bottom: ToastSpacing,
      content: `""`,
      position: 'absolute',
      right: 0,
      top: 0,
      width: 1,
    },
    '&.success::after': {
      backgroundColor: ColorMap.success.accentColor,
    },
    '&.info::after': {
      backgroundColor: ColorMap.info.accentColor,
    },
    '&.warning::after': {
      backgroundColor: ColorMap.warning.accentColor,
    },
    '&.error::after': {
      backgroundColor: ColorMap.error.accentColor,
    },
  }
});

const MessageStyleName = TypeStyle.style({
  margin: ToastSpacing,
  marginTop: 0,
});

const ActionContainerStyleName = TypeStyle.style({
  marginBottom: ToastSpacing,
});

const ActionLinkStyleName = TypeStyle.style({
  marginLeft: ToastSpacing - 1, // -1 allows space for the focus indicator
  marginRight: ToastSpacing * 2,
});

const CloseButtonStyleName = TypeStyle.style({
  flexShrink: 0,
  margin: 0,
  marginLeft: ToastSpacing * 2,
});

export const ToastBaseWidget: React.FC<PropsAndElementAttrs<ToastBaseProps>> = props => {
  // eslint-disable-next-line deprecation/deprecation
  const { actionHref, actionMessage, children, className, message, onAction, onCloseRequest, testId, toastType, ...htmlAttrs } = props;
  const hidden = useInitiallyHiddenElement();

  const prefixText = (Messages as Record<StatusMessageFieldName, () => string>)[toastType + 'ToastPrefix' as StatusMessageFieldName]();

  const handleActionLinkClick = React.useCallback<React.MouseEventHandler>(e => {
    if (onAction || actionHref === DefaultActionHref) {
      e.preventDefault();
      onAction?.();
    }
  }, [actionHref, onAction]);

  return (
    <div
      className={TypeStyle.classes(ToastRootStyleName, AlertBaseColorStyleName, className, toastType)}
      data-tb-test-id={makeTestId(testId, ToastDivTestIdSuffix)}
      {...htmlAttrs}
    >
      <div className={ContentDivStyleName} data-tb-test-id={makeTestId(testId, ToastContentTestIdSuffix)}>
        <StatusIcon type={toastType} className={StatusIconStyleName} />
        <div className={TypeStyle.classes(MessageContainerBaseStyleName, toastType)}>
          <div aria-hidden={hidden} className={MessageStyleName} role='alert' data-tb-test-id={makeTestId(testId, ToastMessageTestIdSuffix)}>
            <span style={{ position: 'absolute', color: 'transparent', zIndex: -1 }}>{prefixText}</span>
            {message}
            {children}
          </div>
          <div className={ActionContainerStyleName}>
            {actionMessage &&
              <TextLinkWidget
                children={actionMessage}
                className={ActionLinkStyleName}
                href={actionHref}
                kind={TextLinkKind.Standalone}
                onClick={handleActionLinkClick}
                target='_blank'
                testId={testId}
              />
            }
          </div>
        </div>
      </div>
      <CloseButtonWidget
        className={CloseButtonStyleName}
        data-tb-test-id='toast-close-Button'
        onClick={onCloseRequest}
        testId={null!} // overridden above in data-tb-test-id
        title={Messages.closeAlert()}
      />
    </div>
  );
};
ToastBaseWidget.displayName = 'ToastBaseWidget';
ToastBaseWidget.defaultProps = { actionHref: DefaultActionHref };
